import React, { useState, useEffect } from 'react';
import { Send, Briefcase, MapPin, Calendar, DollarSign, CheckCircle, Upload, Link as LinkIcon, Info, FileText, ToggleLeft, ToggleRight, Hash, Users, Lock } from 'lucide-react';
import axios from 'axios'; 
import './JobPosting.css';

const emptyState = {
  companyName: '', role: '', batch: '', location: '', typeOfWork: 'Full Time', ctc: '',
  priority: 'P3', 
  requestId: '', beelineId: '', assignedTo: [], 
  applyByDate: '', applyByTime: '', dateOfVisit: '',
  tenth: '', twelfth: '', minCGPA: '', passoutYear: '',
  maxCurrentArrears: '0', maxHistoryArrears: '0', gender: 'Any',
  aboutCompany: '', aboutJob: '', hiringWorkflows: '', externalLink: '', additionalInfo: '',
  selectedJd: '', localJdFile: null, otherFiles: [] 
};

export default function JobPosting({ onPost, availableJDs = [], availableVendors = [], availableRecruiters = [], initialData, isRecruiter = false }) {
  
  const [isBeeline, setIsBeeline] = useState(false);
  const [restrictAccess, setRestrictAccess] = useState(false);
  const [formData, setFormData] = useState(emptyState);

  // --- POPULATE FORM ON EDIT ---
  useEffect(() => {
    if (initialData) {
      const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toISOString().split('T')[0];
      };

      setFormData({
        ...initialData,
        priority: initialData.priority || 'P3',
        applyByDate: formatDate(initialData.applyByDate),
        dateOfVisit: formatDate(initialData.dateOfVisit),
        tenth: initialData.tenth || '',
        twelfth: initialData.twelfth || '',
        minCGPA: initialData.minCGPA || '',
        maxHistoryArrears: initialData.maxHistoryArrears || 0,
        maxCurrentArrears: initialData.maxCurrentArrears || 0,
        requestId: initialData.requestId || '',
        beelineId: initialData.beelineId || '',
        assignedTo: initialData.assignedTo || [],
        _id: initialData._id,
        localJdFile: null 
      });

      const flagIsTrue = initialData.isBeeline === true || initialData.isBeeline === 'true';
      const hasBeelineData = (initialData.beelineId && initialData.beelineId.trim() !== '') || 
                             (initialData.requestId && initialData.requestId.trim() !== '');

      if (flagIsTrue || hasBeelineData) {
          setIsBeeline(true);
      } else {
          setIsBeeline(false);
      }

      // NEW LINE:
      setRestrictAccess((initialData.assignedTo || []).length > 0);

    } else {
      setFormData(emptyState);
      setIsBeeline(false);
      // NEW LINE:
      setRestrictAccess(false);
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setFormData(prev => ({ ...prev, localJdFile: file }));
  };

  const toggleAssignment = (id) => {
    setFormData(prev => {
        const current = prev.assignedTo || [];
        if (current.includes(id)) {
            return { ...prev, assignedTo: current.filter(x => x !== id) };
        } else {
            return { ...prev, assignedTo: [...current, id] };
        }
    });
  };

  const handleJdChange = (e) => {
    const jdId = e.target.value;
    const selected = availableJDs.find(jd => (jd._id || jd.id).toString() === jdId);
    setFormData(prev => ({
      ...prev,
      selectedJd: jdId,
      role: prev.role || (selected ? selected.job_role : ''),
      location: prev.location || (selected ? selected.location : ''),
      aboutCompany: prev.aboutCompany || (selected ? selected.about_company : ''),
      aboutJob: prev.aboutJob || (selected ? selected.roles_responsibilities : '')
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.companyName || !formData.role) {
      alert("Company Name and Role are required.");
      return;
    }

    try {
      const data = new FormData();
      
      Object.keys(formData).forEach(key => {
        if (key === 'isBeeline') return; 

        if (key === 'assignedTo') {
            if (Array.isArray(formData[key])) {
                formData[key].forEach(item => data.append('assignedTo[]', item));
            }
        } else if (key !== 'localJdFile' && key !== '_id' && key !== 'otherFiles' && formData[key] !== null && formData[key] !== undefined) {
          data.append(key, formData[key]);
        }
      });

      data.append('isBeeline', isBeeline);

      if (formData.localJdFile) {
        data.append('localJdFile', formData.localJdFile);
      }

       console.log('🔍 [FRONTEND DEBUG] localJdFile being sent:', formData.localJdFile);

      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      let response;

      if (initialData && initialData._id) {
        response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/jobs/${initialData._id}`, data, config);
      } else {
        response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/jobs`, data, config);
      }

      if (response.status === 200 || response.status === 201) {
        alert("Job Saved Successfully!");
        if(onPost) onPost(response.data); 
      }

    } catch (error) {
      console.error("Error posting job:", error);
      alert("Failed to post job. Please try again.");
    }
  };

  const activeGeneratedJd = availableJDs.find(jd => (jd._id || jd.id).toString() === formData.selectedJd);

  return (
    <div className="job-posting-container">
      <div className="jp-editor">
        <div className="jp-header">
          <h3>{initialData ? 'Edit Job Posting' : 'Create New Job Post'}</h3>
          <div className="beeline-toggle-wrapper" onClick={() => setIsBeeline(!isBeeline)}>
            <span className={!isBeeline ? "active-mode" : ""}>Standard</span>
            {isBeeline ? <ToggleRight size={32} color="#2563eb" /> : <ToggleLeft size={32} color="#94a3b8" />}
            <span className={isBeeline ? "active-mode" : ""}>Beeline (Tata)</span>
          </div>
        </div>

        <form className="jp-form">
          
          {/* BEELINE SPECIFIC FIELDS (Hidden for Standard) */}
          {isBeeline && (
            <div className="beeline-section">
              {!isRecruiter && (
  <>
                <div className="form-section-label" style={{color: '#2563eb', marginTop: 0}}>Beeline Request Details</div>
                <div className="form-row two-col">
                    <div className="form-group">
                        <label>Request ID *</label>
                        <input type="text" name="requestId" value={formData.requestId} onChange={handleChange} placeholder="e.g. REQ-2024-001" style={{borderColor: '#93c5fd', background: '#eff6ff'}} />
                    </div>
                    <div className="form-group">
                        <label>Beeline ID *</label>
                        <input type="text" name="beelineId" value={formData.beelineId} onChange={handleChange} placeholder="e.g. BL-998877" style={{borderColor: '#93c5fd', background: '#eff6ff'}} />
                    </div>
                </div>

                </>
)}
            </div>
          )}

          {/* ALWAYS VISIBLE PARTNER ASSIGNMENT */}
          <div className="form-section-label" style={{marginTop: isBeeline ? '20px' : '0'}}>
             Job Visibility
          </div>
          <label style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'10px', cursor:'pointer', fontSize:'0.9rem', color:'#334155'}}>
              <input
                  type="checkbox"
                  checked={restrictAccess}
                  onChange={(e) => {
                      setRestrictAccess(e.target.checked);
                      if (!e.target.checked) {
                          setFormData(prev => ({ ...prev, assignedTo: [] }));
                      }
                  }}
                  style={{width:'16px', height:'16px', cursor:'pointer'}}
              />
              Restrict this job to specific partners (leave unchecked = visible to all vendors &amp; recruiters, including new ones added later)
          </label>

          {restrictAccess && (
          <div className="form-group">
              <div className="jp-partner-columns" style={{display:'flex', gap:'20px'}}>
                  {/* VENDORS COLUMN */}
                  <div style={{flex:1, border:'1px solid #e2e8f0', borderRadius:'8px', padding:'10px', background:'#f8fafc'}}>
                      <div style={{fontSize:'0.85rem', fontWeight:'600', color:'#334155', marginBottom:'8px', borderBottom:'1px solid #e2e8f0', paddingBottom:'5px'}}>
                          Vendors
                      </div>
                      <div style={{maxHeight:'150px', overflowY:'auto'}}>
                          {availableVendors.length > 0 ? availableVendors.map(v => (
                              <label key={v.id} style={{display:'flex', alignItems:'center', gap:'8px', padding:'4px 0', cursor:'pointer', fontSize:'0.9rem', color:'#334155'}}>
                                  <input 
                                      type="checkbox" 
                                      checked={(formData.assignedTo || []).includes(v.id)} 
                                      onChange={() => toggleAssignment(v.id)}
                                      style={{width:'16px', height:'16px', cursor:'pointer'}}
                                  />
                                  {v.vendorName || v.name}
                              </label>
                          )) : <div style={{fontSize:'0.8rem', color:'#94a3b8', fontStyle:'italic'}}>No Vendors found</div>}
                      </div>
                  </div>

                  {/* RECRUITERS COLUMN */}
                  <div style={{flex:1, border:'1px solid #e2e8f0', borderRadius:'8px', padding:'10px', background:'#f8fafc'}}>
                      <div style={{fontSize:'0.85rem', fontWeight:'600', color:'#334155', marginBottom:'8px', borderBottom:'1px solid #e2e8f0', paddingBottom:'5px'}}>
                          Recruiters
                      </div>
                      <div style={{maxHeight:'150px', overflowY:'auto'}}>
                          {availableRecruiters.length > 0 ? availableRecruiters.map(r => (
                              <label key={r.id} style={{display:'flex', alignItems:'center', gap:'8px', padding:'4px 0', cursor:'pointer', fontSize:'0.9rem', color:'#334155'}}>
                                  <input 
                                      type="checkbox" 
                                      checked={(formData.assignedTo || []).includes(r.id)} 
                                      onChange={() => toggleAssignment(r.id)}
                                      style={{width:'16px', height:'16px', cursor:'pointer'}}
                                  />
                                  {r.recruiterName || r.username || r.name}
                              </label>
                          )) : <div style={{fontSize:'0.8rem', color:'#94a3b8', fontStyle:'italic'}}>No Recruiters found</div>}
                      </div>
                  </div>
              </div>
          </div>
          )}

          <div className="form-section-label">Job Description Source</div>
          <div className="form-row">
            <div className="form-group">
              <label>Select Generated JD</label>
              <select name="selectedJd" value={formData.selectedJd} onChange={handleJdChange}>
                <option value="">-- Select from System --</option>
                {availableJDs.map(jd => (
                  <option key={jd.id || jd._id} value={jd.id || jd._id}>{jd.job_role} - {jd.location}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Or Upload Local JD (PDF/Doc)</label>
              <div className="file-upload-wrapper">
                <input type="file" onChange={handleFileChange} className="custom-file-input" accept=".pdf,.doc,.docx" />
                <span className="file-name">{formData.localJdFile ? formData.localJdFile.name : "No file chosen"}</span>
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Hiring Priority</label>
              <select name="priority" value={formData.priority} onChange={handleChange} style={{ borderColor: formData.priority === 'P1' ? '#ef4444' : '#cbd5e1' }}>
                <option value="P1">P1 - High Priority (Urgent)</option>
                <option value="P2">P2 - Medium Priority</option>
                <option value="P3">P3 - Low Priority</option>
              </select>
            </div>
          </div>

          <div className="form-section-label">Job Details</div>
          <div className="form-row">
            <div className="form-group">
              <label>Company Name *</label>
              <input type="text" name="companyName" value={formData.companyName} onChange={handleChange} placeholder="e.g. Google" />
            </div>
            <div className="form-group">
              <label>Job Role *</label>
              <input type="text" name="role" value={formData.role} onChange={handleChange} placeholder="e.g. SDE I" />
            </div>
          </div>

          <div className="form-row three-col">
            <div className="form-group"><label>Batch</label><input type="text" name="batch" value={formData.batch} onChange={handleChange} placeholder="e.g. 2024, 2025" /></div>
            <div className="form-group">
              <label>Work Type</label>
              <select name="typeOfWork" value={formData.typeOfWork} onChange={handleChange}>
                <option value="Full Time">Full Time</option>
                <option value="Internship">Internship</option>
                <option value="Contract">Contract</option>
                <option value="Freelancer">Freelancer</option>
              </select>
            </div>
            <div className="form-group"><label>CTC / Stipend</label><input type="text" name="ctc" value={formData.ctc} onChange={handleChange} placeholder="e.g. 12 LPA" /></div>
          </div>

          <div className="form-group">
            <label>Job Location</label>
            <input type="text" name="location" value={formData.location} onChange={handleChange} placeholder="e.g. Hyderabad, India" />
          </div>

          <div className="form-section-label">Eligibility Criteria</div>
          <div className="form-row four-col">
             <div className="form-group"><label>10th %</label><input type="number" name="tenth" value={formData.tenth} onChange={handleChange} placeholder="60" /></div>
             <div className="form-group"><label>12th %</label><input type="number" name="twelfth" value={formData.twelfth} onChange={handleChange} placeholder="60" /></div>
             <div className="form-group"><label>Min CGPA</label><input type="number" step="0.1" name="minCGPA" value={formData.minCGPA} onChange={handleChange} placeholder="7.0" /></div>
             <div className="form-group"><label>Passout Year</label><input type="text" name="passoutYear" value={formData.passoutYear} onChange={handleChange} placeholder="2024" /></div>
          </div>

          <div className="form-row three-col">
            <div className="form-group"><label>History Arrears</label><input type="number" name="maxHistoryArrears" value={formData.maxHistoryArrears} onChange={handleChange} /></div>
            <div className="form-group"><label>Current Arrears</label><input type="number" name="maxCurrentArrears" value={formData.maxCurrentArrears} onChange={handleChange} /></div>
             <div className="form-group">
              <label>Gender Allowed</label>
              <select name="gender" value={formData.gender} onChange={handleChange}>
                <option value="Any">Any</option>
                <option value="Male">Male Only</option>
                <option value="Female">Female Only</option>
              </select>
            </div>
          </div>

          <div className="form-section-label">Timeline</div>
          <div className="form-row three-col">
            <div className="form-group"><label>Apply By Date</label><input type="date" name="applyByDate" value={formData.applyByDate} onChange={handleChange} /></div>
             <div className="form-group"><label>Apply By Time</label><input type="time" name="applyByTime" value={formData.applyByTime} onChange={handleChange} /></div>
             <div className="form-group"><label>Date of Visit (Tentative)</label><input type="date" name="dateOfVisit" value={formData.dateOfVisit} onChange={handleChange} /></div>
          </div>

          <div className="form-section-label">Detailed Information</div>
          <div className="form-group"><label>About Company</label><textarea name="aboutCompany" rows="4" value={formData.aboutCompany} onChange={handleChange} placeholder="Company description..."></textarea></div>
          <div className="form-group"><label>About Job / Responsibilities</label><textarea name="aboutJob" rows="5" value={formData.aboutJob} onChange={handleChange} placeholder="Describe the role..."></textarea></div>
          <div className="form-group"><label>Hiring Workflow (Steps)</label><textarea name="hiringWorkflows" rows="3" value={formData.hiringWorkflows} onChange={handleChange} placeholder="e.g. Online Test -> Technical Interview -> HR"></textarea></div>
          <div className="form-group"><label>External Application Link</label><input type="text" name="externalLink" value={formData.externalLink} onChange={handleChange} placeholder="https://..." /></div>
          <div className="form-group"><label>Additional Instructions</label><textarea name="additionalInfo" rows="2" value={formData.additionalInfo} onChange={handleChange} placeholder="Any other instructions..."></textarea></div>

           <div className="form-group"><label>Other Attachments (Optional)</label><input type="file" multiple className="file-input" /></div>
        </form>
      </div>

      <div className="jp-preview-pane">
        <div className="preview-header"><h3>Live Job Preview</h3></div>
        
        {/* PRIVACY BADGE FOR ALL JOBS */}
        {formData.assignedTo.length > 0 && (
            <div style={{background: '#fef2f2', color: '#991b1b', padding: '10px 12px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', fontWeight: '600'}}>
                <Lock size={16} /> Restricted Visibility ({formData.assignedTo.length} Partners Assigned)
            </div>
        )}

        {isBeeline && (
            <div style={{background: '#eff6ff', color: '#1e40af', padding: '12px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #bfdbfe', display: 'flex', flexDirection: 'column', gap: '4px'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem', fontWeight: '700'}}>
                    <Hash size={14}/> Beeline Details
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#3b82f6'}}>
                    <span>Req ID: <strong>{formData.requestId || '...'}</strong></span>
                    <span>Beeline ID: <strong>{formData.beelineId || '...'}</strong></span>
                </div>
            </div>
        )}

        <div className="job-card-preview">
          <div className="jcp-header">
            <div className="company-logo-placeholder">{formData.companyName ? formData.companyName.charAt(0) : 'C'}</div>
            <div className="jcp-title-block">
                <h2>{formData.role || 'Job Role'}</h2>
                <p>{formData.companyName || 'Company Name'}</p>
            </div>
            <div className="jcp-badge">{formData.typeOfWork}</div>
          </div>
          <div className="jcp-stats">
            <div className="stat-item"><DollarSign size={16} /><span>{formData.ctc || 'CTC'}</span></div>
            <div className="stat-item"><MapPin size={16} /><span>{formData.location || 'Location'}</span></div>
            <div className="stat-item"><Briefcase size={16} /><span>{formData.batch ? `Batch: ${formData.batch}` : 'Batch'}</span></div>
          </div>
          <div className="jcp-divider"></div>

          <div className="jcp-section">
            <h4><CheckCircle size={16} /> Eligibility Criteria</h4>
            <div className="criteria-grid">
              <div className="crit-item"><span className="crit-label">10th</span><span className="crit-val">{formData.tenth ? `${formData.tenth}%` : '--'}</span></div>
              <div className="crit-item"><span className="crit-label">12th</span><span className="crit-val">{formData.twelfth ? `${formData.twelfth}%` : '--'}</span></div>
              <div className="crit-item"><span className="crit-label">CGPA</span><span className="crit-val">{formData.minCGPA || '--'}</span></div>
              <div className="crit-item"><span className="crit-label">Arrears</span><span className="crit-val">{formData.maxHistoryArrears}/{formData.maxCurrentArrears}</span></div>
              <div className="crit-item"><span className="crit-label">Gender</span><span className="crit-val">{formData.gender}</span></div>
              <div className="crit-item"><span className="crit-label">Passout</span><span className="crit-val">{formData.passoutYear || '--'}</span></div>
            </div>
          </div>

           <div className="jcp-section highlight-section">
            <h4><Calendar size={16} /> Timeline</h4>
            <div className="timeline-row">
               <div><span className="t-label">Apply By:</span><span className="t-val">{formData.applyByDate || '--'} {formData.applyByTime}</span></div>
               <div><span className="t-label">Visit Date:</span><span className="t-val">{formData.dateOfVisit || 'TBD'}</span></div>
            </div>
          </div>

          {formData.aboutCompany && (<div className="jcp-section"><h4>About Company</h4><p className="jcp-desc">{formData.aboutCompany}</p></div>)}
          <div className="jcp-section"><h4>About the Job</h4><p className="jcp-desc">{formData.aboutJob || 'Job details will appear here...'}</p></div>
          <div className="jcp-section"><h4>Hiring Workflow</h4><p className="jcp-desc workflow-text">{formData.hiringWorkflows || 'Not specified'}</p></div>
          
          {(formData.externalLink || formData.additionalInfo) && (
            <div className="jcp-section" style={{ background: '#f8fafc', padding: '10px', borderRadius: '8px' }}>
              <h4><Info size={16} /> Additional Details</h4>
              {formData.externalLink && (<div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '5px', overflow: 'hidden' }}><LinkIcon size={14} color="#2563eb" /><a href={formData.externalLink} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9rem', color: '#2563eb', textDecoration: 'underline' }}>{formData.externalLink}</a></div>)}
              {formData.additionalInfo && (<p className="jcp-desc" style={{ fontSize: '0.85rem', fontStyle: 'italic' }}>Note: {formData.additionalInfo}</p>)}
            </div>
          )}
          
          {(formData.localJdFile || activeGeneratedJd) && (
             <div className="jcp-section" style={{ marginTop: '15px', borderTop: '1px solid #e2e8f0', paddingTop: '10px' }}>
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><FileText size={16} /> Attached JD Document</h4>
                {formData.localJdFile && (
                  <div style={{ padding: '10px', background: '#f0fdf4', borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#166534', fontSize: '0.85rem', marginTop: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                       <Upload size={16} />
                       <div><div style={{ fontWeight: '600' }}>{formData.localJdFile.name}</div><div style={{ fontSize: '0.75rem', opacity: 0.8 }}>Manually Uploaded</div></div>
                    </div>
                    <a href={URL.createObjectURL(formData.localJdFile)} target="_blank" rel="noopener noreferrer" style={{ background: '#fff', padding: '4px 10px', borderRadius: '4px', fontSize: '0.75rem', textDecoration: 'none', color: '#166534', border: '1px solid #bbf7d0' }}>Open PDF</a>
                  </div>
                )}
                {(!formData.localJdFile && activeGeneratedJd) && (
                   <div style={{ padding: '10px', background: '#eff6ff', borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#1e40af', fontSize: '0.85rem', marginTop: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                       <FileText size={16} />
                       <div><div style={{ fontWeight: '600' }}>{activeGeneratedJd.job_role} (Generated)</div><div style={{ fontSize: '0.75rem', opacity: 0.8 }}>System Generated JD</div></div>
                    </div>
                    <a href={activeGeneratedJd.fileUrl || '#'} target="_blank" rel="noopener noreferrer" style={{ background: '#fff', padding: '4px 10px', borderRadius: '4px', fontSize: '0.75rem', textDecoration: 'none', color: '#1e40af', border: '1px solid #bfdbfe' }}>Open PDF</a>
                  </div>
                )}
             </div>
          )}
        </div>
        <div className="preview-footer">
           <button className="post-job-btn" onClick={handleSubmit}><Send size={18} /> {initialData ? 'Update Job' : 'Post Job'}</button>
        </div>
      </div>
    </div>
  );
}

